/**
 * Heat-cell colouring, computed from utilisation ratios. Pure.
 *
 * Sequential-by-design (one hue, light→dark) below the threshold, switching
 * family to coral at/above it — the coral is a STATE (over capacity), not a
 * darker step of the same ramp, and every cell also prints its number and an
 * over-dot so the state is never colour-alone. All colours are derived from
 * design tokens via `color-mix()` — raw rgba()/hex literals are banned by
 * `theme/designSystem.unit.ts`.
 */

export type HeatCellColor = {
  background: string;
  /** Token expression for the value text inside the cell. */
  foreground: string;
  over: boolean;
};

const mix = (token: string, percent: number): string =>
  `color-mix(in srgb, var(${token}) ${String(Math.round(percent))}%, transparent)`;

/** The over-capacity boundary: workload = capacity. */
const OVER = 1;

/**
 * Utilisation heat: blue alpha ramp from faint to strong as the ratio
 * approaches 1, coral past it (deeper the further over). `ratio` null =
 * load with no capacity — rendered as fully over.
 */
export const heatCellColor = (ratio: number | null, hours: number): HeatCellColor => {
  if (hours <= 0) return { background: 'transparent', foreground: 'var(--fg-3)', over: false };
  const effective = ratio ?? Number.POSITIVE_INFINITY;
  if (effective >= OVER) {
    const overshoot = Number.isFinite(effective) ? effective - OVER : 1;
    const alpha = Math.min(92, 50 + overshoot * 90);
    return {
      background: mix('--color-coral-80', alpha),
      foreground: alpha > 50 ? 'var(--color-black-00)' : 'var(--color-coral-100)',
      over: true,
    };
  }
  const alpha = 10 + effective * 40;
  return {
    background: mix('--color-blue-80', alpha),
    foreground: effective > 0.62 ? 'var(--color-blue-100)' : 'var(--fg-2)',
    over: false,
  };
};

/** Hours cells on allocation drill rows: a faint blue share-of-capacity tint. */
export const hoursCellColor = (hours: number, capacityHours: number): HeatCellColor => {
  if (hours <= 0) return { background: 'transparent', foreground: 'var(--fg-3)', over: false };
  const share = capacityHours > 0 ? Math.min(1, hours / capacityHours) : 0.55;
  return {
    background: mix('--color-blue-80', 6 + share * 30),
    foreground: 'var(--fg-2)',
    over: false,
  };
};

/** Generic (unassigned) demand: the pink tint that marks unstaffed hours. */
export const demandCellColor = (hours: number): HeatCellColor => {
  if (hours <= 0) return { background: 'transparent', foreground: 'var(--fg-3)', over: false };
  return {
    background: mix('--color-pink-80', 16),
    foreground: 'var(--color-pink-100)',
    over: false,
  };
};

/** The toolbar legend's swatches, faint → over. */
export const HEAT_LEGEND_STEPS: readonly string[] = [
  mix('--color-blue-80', 14),
  mix('--color-blue-80', 30),
  mix('--color-blue-80', 46),
  mix('--color-coral-80', 75),
];
